import { useState, useEffect } from "react";
import adminClasses from "../../../landing-pages/admin/admin.module.css";
import IAIAdminDashboardModel from "../../../models/admin/dashboard/response/IAIAdminDashboardModel";
import modalUtils from "../../../utils/modal-utils";
import toasterUtils from "../../../utils/toaster-utils";
import adminDashboardService from "../../../services/admin/admin-dashboard-service";
import loaderUtils from "../../../utils/loader-utils";
import IAIAdminSearchInputModel from "../../../models/admin/dashboard/request/IAIAdminSearchInputModel";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import dateTimeUtils from "../../../utils/date-utils";
import RecruiterDashboardModel from "../../../models/company/dashboard/response/RecruiterDashboardModel";
import RecruiterDashboardRequestModel from "../../../models/company/dashboard/request/RecruiterDashboardRequestModel";
import companyDashboardService from "../../../services/company/dashboard-service";
import { useAppSelector } from "../../../context-store";
import { GeneralPoupConfig, ModalSize } from "../../../models/modal-types";
import ViewEscalationDashboardComponent from "./ViewEscalationsDashboardComponent";
import TodayInterviewsGridComponent from "./TodayInterviewsGridComponent";
import "./RecruiterDashboardComponent.css"

const RecruiterDashboardComponent = () => {
    let loggedInUser = useAppSelector((state) => state.authState.loggedInUser);
    const [recruiterDasgboardDetails, setRecruiterDasgboardDetails] = useState<RecruiterDashboardModel | null>(null);
    const [refreshDashboard, setRefreshDashboard] = useState<number>(2);
    const [startDateSearch, setStartDateSearch] = useState<Dayjs | null>(dayjs());
    const [endDateSearch, setEndDateSearch] = useState<Dayjs | null>(dayjs());

    useEffect(() => {
        if (loggedInUser) {
            getRecruiterDashboardDetails(loggedInUser.profileId);
        }
    }, []);

    const searchDashboard = () => {
        if (startDateSearch === null || endDateSearch === null) {
            toasterUtils.showError("Please select Start Date and End Date");
            return;
        } else {
            if (loggedInUser) {
                getRecruiterDashboardDetails(loggedInUser.profileId);
            }
        }
    }

    const getRecruiterDashboardDetails = (recruiterId: string) => {
        let request: RecruiterDashboardRequestModel = {
            startDate: dateTimeUtils.getFormattedDate(startDateSearch ? startDateSearch.toDate() : new Date()),
            endDate: dateTimeUtils.getFormattedDate(endDateSearch ? endDateSearch.toDate() : new Date()),
            recruitersId: [recruiterId],
            userId: null
        }
        setRefreshDashboard(2);
        modalUtils.showLoader();
        companyDashboardService.getRecruiterDashboard(request).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setRecruiterDasgboardDetails(res.data);
            setRefreshDashboard(refreshDashboard * 5);
        }).catch((error) => {
            modalUtils.closeModal();
            setRefreshDashboard(refreshDashboard * 5);
            toasterUtils.showError(error);
        });
    };

    const clearSearch = () => {
        setStartDateSearch(dayjs().subtract(15, "day"));
        setEndDateSearch(dayjs());
        if (loggedInUser) {
            getRecruiterDashboardDetails(loggedInUser.profileId);
        }
    };

    const onViewEscalationsHandler = () => {
        if (loggedInUser) {
            const config: GeneralPoupConfig = {
                title: "Escalations",
                content: (
                    <ViewEscalationDashboardComponent
                        recruiterId={loggedInUser?.profileId} />
                ),
                size: ModalSize.XL,
                className: "model-min-height-500"
            };
            modalUtils.showPopup(config);
        }
    };

    const viewTodaysInterviews = () => {
        if (loggedInUser) {
            let request: RecruiterDashboardRequestModel = {
                startDate: dateTimeUtils.getFormattedDate(startDateSearch ? startDateSearch.toDate() : new Date()),
                endDate: dateTimeUtils.getFormattedDate(endDateSearch ? endDateSearch.toDate() : new Date()),
                recruitersId: [loggedInUser?.profileId],
                userId: null
            }
            setRefreshDashboard(2);
            modalUtils.showLoader();
            companyDashboardService.getTodayInterviews(request).then((res) => {
                modalUtils.closeModal();
                if (!res.isSuccess || res.errorMessages.length > 0) {
                    toasterUtils.showError(res.errorMessages.toString());
                    return;
                }
                const config: GeneralPoupConfig = {
                    title: "Today's Interviews",
                    content: (
                        <TodayInterviewsGridComponent
                            interviews={res.items} />
                    ),
                    size: ModalSize.XL,
                    className: "model-min-height-500"
                };
                modalUtils.showPopup(config);
            }).catch((error) => {
                modalUtils.closeModal();
                toasterUtils.showError(error);
            });
        }
    };

    return (
        <div>
            <div className="card card-white">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Start Date"
                                    value={startDateSearch}
                                    onChange={(newValue) => setStartDateSearch(newValue)}
                                    format="YYYY-MMM-DD"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            margin: "normal",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </div>
                        <div className="col-md-3">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="End Date"
                                    value={endDateSearch}
                                    onChange={(newValue) => setEndDateSearch(newValue)}
                                    format="YYYY-MMM-DD"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            margin: "normal",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </div>
                        <div className="col-md-6 button-aligned-right">
                            <Button variant="contained" color="success" onClick={() => searchDashboard()} startIcon={<SearchIcon />}>
                                Search
                            </Button>
                            <Button variant="contained" color="warning" onClick={() => clearSearch()} startIcon={<ClearIcon />} sx={{ ml: 2 }}>
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="dashboard-container">
                <div className="dashboard-grid">
                    <div className="dashboard-card">
                        <h3>No of profiles Added</h3>
                        <p className="dashboard-number">
                            <span>
                                {recruiterDasgboardDetails?.noOfCandidatesAdded}
                            </span>
                        </p>
                    </div>
                    <div className="dashboard-card">
                        <h3>Number of Interviews Scheduled</h3>
                        <p className="dashboard-number">
                            <span>
                                {recruiterDasgboardDetails?.noOfInterviewsScheduled}
                            </span>
                        </p>
                    </div>
                    <div className="dashboard-card">
                        <h3>Number of Interviews Rated</h3>
                        <p className="dashboard-number">
                            <span>
                                {recruiterDasgboardDetails?.noOfInterviewsRated}
                            </span>
                        </p>
                    </div>
                    <div className="dashboard-card">
                        <h3>Escalations</h3>
                        <p className="dashboard-number">
                            <span>
                                <a onClick={() => onViewEscalationsHandler()}>{recruiterDasgboardDetails?.noOfEscalations}</a>
                            </span>
                        </p>
                    </div>
                    <div className="dashboard-card">
                        <h3>Today's Interviews</h3>
                        <p className="dashboard-number">
                            <span>
                                <a onClick={() => viewTodaysInterviews()}>{recruiterDasgboardDetails?.todayInterviews}</a>
                            </span>
                        </p>
                    </div>
                    <div className="dashboard-card">
                        <h3>No Of Postings</h3>
                        <p className="dashboard-number">
                            <span>
                                {recruiterDasgboardDetails?.noOfPostings}
                            </span>
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
};
export default RecruiterDashboardComponent;